import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "./lib/cn.js";

/**
 * Button — chrome-vocabulary inline action element.
 *
 * Sharp corners + hairline outline + mono caps with --pp-tracking-caps.
 * Chamfer is intentionally NOT applied here — chamfered silhouettes are
 * reserved for chrome containers (Card, Dialog, Panel). Buttons are
 * inline interactive elements; sharp + outlined reads as a terminal
 * action key, not a UI chrome surface.
 *
 * Variants:
 *   - `default`    — solid filled with theme primary; text on accent.
 *   - `destructive`— solid danger fill.
 *   - `accent`     — solid theme accent.
 *   - `outline`    — hairline border, transparent fill. The canonical
 *                    scifi button look — outlined monospace caps.
 *   - `secondary`  — translucent strong-bg fill.
 *   - `ghost`      — bare; hover paints strong-bg.
 *   - `link`       — text-only, underline on hover.
 */
const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2 whitespace-nowrap",
    "font-[var(--pp-font-family)] font-[var(--pp-weight-bold)] uppercase tracking-[var(--pp-tracking-caps)] leading-none",
    "select-none",
    "transition-[background-color,color,border-color,box-shadow,transform]",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pp-accent-a)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--background)]",
    "disabled:pointer-events-none disabled:opacity-50",
  ],
  {
    variants: {
      variant: {
        default:
          "bg-[var(--primary)] text-[var(--pp-text-on-accent)] border border-[var(--primary)] hover:bg-[var(--color-primary-600)] hover:border-[var(--color-primary-600)] active:bg-[var(--color-primary-700)]",
        destructive:
          "bg-[var(--pp-danger)] text-[var(--pp-text-on-accent)] border border-[var(--pp-danger)] hover:opacity-90 active:opacity-80",
        accent:
          "bg-[var(--accent)] text-[var(--pp-text-on-accent)] border border-[var(--accent)] hover:bg-[var(--color-accent-600)] hover:border-[var(--color-accent-600)] active:bg-[var(--color-accent-700)]",
        outline:
          "bg-transparent text-[var(--pp-text)] border border-[var(--pp-border)] hover:border-[var(--pp-text)] hover:bg-[var(--pp-bg)]",
        secondary:
          "bg-[var(--pp-bg-strong)] text-[var(--pp-text)] border border-[var(--pp-border)] hover:border-[var(--pp-text)]",
        ghost:
          "bg-transparent text-[var(--pp-text)] border border-transparent hover:bg-[var(--pp-bg-strong)]",
        link:
          "bg-transparent text-[var(--pp-text)] border border-transparent underline-offset-4 hover:underline p-0 h-auto",
      },
      size: {
        sm: "h-7 px-3 text-[var(--pp-font-sm)]",
        default: "h-9 px-4 text-[var(--pp-font-md)]",
        lg: "h-11 px-6 text-[var(--pp-font-lg)]",
        icon: "h-9 w-9 p-0",
      },
    },
    compoundVariants: [
      { variant: "link", size: "sm", class: "h-auto p-0" },
      { variant: "link", size: "default", class: "h-auto p-0" },
      { variant: "link", size: "lg", class: "h-auto p-0" },
      { variant: "link", size: "icon", class: "h-auto w-auto p-0" },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Visual treatment. Each variant maps to a CSS-var-driven look. */
  variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link" | "accent";
  /** Height / padding scale. */
  size?: "sm" | "default" | "lg" | "icon";
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, type = "button", ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      data-pp-button
      data-pp-variant={variant ?? "default"}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  ),
);

Button.displayName = "Button";

export { buttonVariants };
